<template>
  <div :class="['battle-log', isDark ? 'dark' : 'light']">
    <div
      v-for="(item, index) in recordings"
      :key="index"
      class="log-row"
    >
      <!-- 战斗回合 -->
      <template v-if="(item.type === 'combat' || (!item.type && item.attacker)) && item.hurtType != 10">
        <span class="round">第{{ item.round }}回合</span>
        <!-- 攻击者 -->
        <span :class="item.identity == 0 ? 'player' : 'enemy'">
          {{ item.attacker }}
        </span>

        <!-- 技能/普攻 -->
        <span v-if="item.isSkill == 1" class="skill">
          <!-- 强制普攻显示 -->
          <span v-if="item.skillName && item.skillName.includes('强制普攻')">
            {{ item.skillName }}
          </span>
          <span v-else>
            [{{ item.skillName }}]
            <!-- 多段攻击显示 -->
            <span v-if="item.hitIndex && item.totalHits">
              ({{ item.hitIndex }}/{{ item.totalHits }})
            </span>
          </span>
        </span>
        <span v-else class="normal">
          <span v-if="item.hurtType == 3" class="skill">
            [{{ item.skillName }}] <span class="normal">持续</span>
          </span>
          <span v-else class="normal">
            {{ getActionLabel(item) }}
          </span>
        </span>

        <!-- 目标 -->
        <span>→</span>
        <span class="target">{{ item.hinjured }}</span>

        <!-- 战斗结果动作 -->
        <span :class="getCombatValueClass(item)">
          {{ getCombatValueText(item) }}
        </span>

        <!-- 伤害类型补充 -->
        <span v-if="item.attElement" class="element" :style="{ color: getElementColor(item.attElement) }">
          {{ item.attElement }}
        </span>
        <span v-else-if="getDamageTypeLabel(item)" class="damage-type">
         {{ getDamageTypeLabel(item) }}
        </span>

        <!-- 特殊标记 -->
        <span v-if="item.isCritical == 1" class="tag critical">暴击</span>
        <span v-if="item.comboCount > 0" class="tag combo">连击x{{ item.comboCount }}</span>

        <!-- ========== 新增标记 ========== -->
        <span v-if="item.forceCrit" class="tag critical">强制暴击</span>
        <span v-if="item.fixedComboCount > 0" class="tag combo">连击x{{ item.fixedComboCount }}</span>
        <span v-if="item.lifeStealAmount > 0" class="tag critical">吸血恢复{{ item.lifeStealAmount }}</span>
        <span v-if="isPositiveNumber(item.shieldAdded)" class="tag shield">{{ getShieldAddedLabel(item) }}</span>
        <span v-if="isPositiveNumber(item.shieldAbsorbed)" class="tag shield">护盾抵消{{ item.shieldAbsorbed }}</span>
        <span v-if="item.hasKillSpread" class="tag combo">击杀扩散</span>
        <span v-if="item.dotSettled" class="tag critical">结算</span>
        <span v-if="item.extraComboRatio > 0" class="tag combo">连击增伤+{{ formatPercent(item.extraComboRatio) }}</span>
        <span v-if="item.timeCategory" class="tag timing">{{ getTimeCategoryLabel(item.timeCategory) }}</span>

        <!-- 结果 -->
        <span class="result">
          剩余{{ item.surplusHealth }}血
          <span v-if="item.surplusShieldValue > 0">/当前护盾{{ item.surplusShieldValue }}</span>
          <span v-if="item.isDeath == 1" class="dead">[阵亡]</span>

          <span v-if="hasTriggeredBuffs(item)" class="trigger-text"> 触发:{{ formatTriggeredSummary(item) }}</span>
          <span v-if="hasActiveBuffs(item)" class="active-text"> 生效:{{ formatActiveBuffSummary(item) }}</span>
          <span
            v-if="hasTriggeredBuffs(item) || hasActiveBuffs(item)"
            class="buff-toggle"
            @click="toggleBuffExpand(index)"
          >
            [{{ isBuffExpanded(index) ? '收起' : '展开' }}]
          </span>
          <span v-else-if="item.buffDesc">{{ item.buffDesc }}</span>
          <span v-if="isBuffExpanded(index)" class="buff-detail">
            <span v-if="hasTriggeredBuffs(item)" class="trigger-text"> 触发详情:{{ formatTriggeredDetail(item) }}</span>
            <span v-if="hasActiveBuffs(item)" class="active-text"> 生效详情:{{ formatActiveBuffDetail(item) }}</span>
          </span>
        </span>

        <span v-if="item.controlEffect" class="result">[{{ item.controlEffect }}]</span>
        <span v-if="item.hpCost > 0" class="result">[耗血{{ item.hpCost }}]</span>
        <span v-if="item.extraDamage > 0" class="result">[额外{{ item.extraDamage }}]</span>
        <span v-if="item.delayedHeal > 0" class="result">[延迟治疗{{ item.delayedHeal }}]</span>
        <span v-if="shouldShowSkillDesc(item)" class="result">[{{ item.skillDesc }}]</span>
      </template>

      <!-- ========== 新增：回合状态记录 ========== -->
      <template v-else-if="item.hurtType == 10">
        <span class="round">第{{ item.round }}回合</span>
        <span :class="item.identity == 0 ? 'player' : 'enemy'">
          {{ item.attacker }}
        </span>
        <span class="normal">{{ getStatusText(item) }}</span>
        <span v-if="item.activeBuffList" class="result">
          {{ formatBuffList(item.activeBuffList) }}
        </span>
      </template>

      <!-- 治疗/增益 -->
      <template v-else-if="item.type === 'gain'">
        <span class="round">第{{ item.round }}回合</span>
        <span class="heal">{{ getGainText(item) }}</span>
        <span>
          剩余{{ item.surplusHealth }}血
          <span v-if="item.surplusShieldValue > 0">/当前护盾{{ item.surplusShieldValue }}</span>
        </span>
        <span v-if="isPositiveNumber(item.shieldAdded)" class="tag shield">{{ getShieldAddedLabel(item) }}</span>
        <span v-if="hasTriggeredBuffs(item)" class="trigger-text"> 触发:{{ formatTriggeredSummary(item) }}</span>
        <span v-if="hasActiveBuffs(item)" class="active-text"> 生效:{{ formatActiveBuffSummary(item) }}</span>
        <span
          v-if="hasTriggeredBuffs(item) || hasActiveBuffs(item)"
          class="buff-toggle"
          @click="toggleBuffExpand(index)"
        >
          [{{ isBuffExpanded(index) ? '收起' : '展开' }}]
        </span>
        <span v-else-if="item.buffDesc">{{ item.buffDesc }}</span>
        <span v-if="isBuffExpanded(index)" class="buff-detail">
          <span v-if="hasTriggeredBuffs(item)" class="trigger-text"> 触发详情:{{ formatTriggeredDetail(item) }}</span>
          <span v-if="hasActiveBuffs(item)" class="active-text"> 生效详情:{{ formatActiveBuffDetail(item) }}</span>
        </span>
        <span v-if="shouldShowSkillDesc(item)" class="result">[{{ item.skillDesc }}]</span>
      </template>

      <!-- 战斗结果 -->
      <template v-else-if="item.type === 'result'">
        <span class="result-line">【{{ item.result }}】 剩余血量:{{ item.reHealth }} 蓝量:{{ item.reMana }} {{ item.exp }} {{ item.goods }}</span>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: "battleProcess",
  props: ["recordings"],
  data() {
    return {
      isDark: false,
      processedRecordings: [],
      listKey: 0,
      scrollTop: 0,
      expandedBuffRows: {}
    };
  },
 // 关键修复：添加 watch 监听 recordings 变化
 watch: {
    recordings: {
      immediate: true,
      handler(newVal) {
        console.log('【调试】battle-process recordings changed:', newVal.length);
        if (newVal && newVal.length > 0) {
          this.processData();
          this.$nextTick(() => {
            const container = this.$el.querySelector('.battle-log');
            if (container) {
              container.scrollTop = 0;
            }
          });
        }
      }
    }
  },
  activated() {
    console.log('组件激活');
    this.listKey++;
    // 保留 activated 用于 keep-alive 场景，但主要逻辑移到 watch
  },

  deactivated() {
    const container = this.$el.querySelector('.battle-log');
    if (container) {
      this.scrollTop = container.scrollTop;
    }
  },


  created() {
    const theme = localStorage.getItem("isTheme");
    this.isDark = theme === "black";
    this.$bus.$on('themeChanged', (isDark) => {
      this.isDark = isDark;
    });
  },

  beforeDestroy() {
    this.$bus.$off('themeChanged');
  },

  methods: {
     // 添加 processData 方法
    processData() {
      console.log('【调试】processData called');
      this.processedRecordings = this.recordings || [];
      this.expandedBuffRows = {};
      this.listKey++; // 强制刷新列表
    },
    getActionLabel(item) {
      if (!item) return '普攻';
      if (item.hurtType == 4) return '反击';
      if (item.hurtType == 5) return '恢复';
      if (item.hurtType == 8) return '护盾';
      if (item.hurtType == 10) return '效果';
      return '普攻';
    },
    getDamageTypeLabel(item) {
      if (!item) return '';
      if (this.isImmuneRecord(item)) return '';
      if (item.hurtType == 3) return '持续伤害';
      if (item.hurtType == 4) return '反击伤害';
      if (item.hurtType == 5) {
        return item.skillName === '持续治疗' ? '持续恢复' : '生命恢复';
      }
      if (item.hurtType == 8) return '';
      if (item.hurtType == 10) return '状态效果';
      return item.hurtType === 1 ? '力道伤害' : '灵力伤害';
    },
    getCombatValueClass(item) {
      if (this.isImmuneRecord(item)) return 'immune';
      if (item && (item.hurtType == 5 || item.hurtType == 8)) return 'heal';
      return 'damage';
    },
    getCombatValueText(item) {
      if (!item) return '';
      const hurt = this.getRecordNumber(item.hurt || item.hurtNum || item.damage);

      if (this.isImmuneRecord(item)) {
        return '本次伤害被免疫';
      }
      if (item.hurtType == 8) {
        if (this.isPositiveNumber(item.shieldAdded)) {
          return `获得${item.shieldAdded}点护盾`;
        }
        return hurt > 0 ? `护盾变化${hurt}点` : '护盾效果生效';
      }
      if (item.hurtType == 5) {
        return `恢复${hurt}点生命`;
      }
      if (item.hurtType == 4) {
        return this.isThornsRecord(item) ? `反伤${hurt}点` : `反击${hurt}点`;
      }
      if (item.hurtType == 3) {
        return `持续伤害${hurt}点`;
      }

      const rawHurt = this.getRawHurtText(item);
      return `造成${rawHurt || hurt}点`;
    },
    getGainText(item) {
      if (!item) return '';
      const skillName = item.skillName || '效果';
      const target = item.hinjured || '';
      const hurt = this.getRecordNumber(item.hurt || item.hurtNum || item.damage);
      if (item.hurtType == 8 || this.isPositiveNumber(item.shieldAdded)) {
        const shield = this.isPositiveNumber(item.shieldAdded) ? item.shieldAdded : hurt;
      return `${item.attacker} 使用 ${skillName} 为 ${target} 获得 ${shield}点护盾`;
      }
      return `${item.attacker} 使用 ${skillName} 恢复 ${target} ${hurt}点生命`;
    },
    getStatusText(item) {
      if (!item) return '';
      return item.skillDesc || item.buffDesc || item.controlEffect || item.skillName || '状态效果';
    },
    getShieldAddedLabel(item) {
      if (!item) return '';
      const desc = `${item.skillDesc || ''}${item.skillName || ''}`;
      if (desc.indexOf('吸血转化护盾') !== -1) return `吸血转化护盾+${item.shieldAdded}`;
      if (desc.indexOf('回血转化护盾') !== -1 || desc.indexOf('套装回血转化护盾') !== -1) return `回血转化护盾+${item.shieldAdded}`;
      if (desc.indexOf('反击转化护盾') !== -1) return `反击转化护盾+${item.shieldAdded}`;
      return `护盾+${item.shieldAdded}`;
    },
    getRecordNumber(value) {
      if (value === null || value === undefined || value === '') return 0;
      const text = String(value);
      const matches = text.match(/-?\d+/g);
      if (!matches) return 0;
      return matches.reduce((sum, item) => {
        const num = Number(item);
        return sum + (Number.isFinite(num) ? Math.max(num, 0) : 0);
      }, 0);
    },
    getRawHurtText(item) {
      if (!item) return '';
      const value = item.hurt || item.damage || item.hurtNum;
      if (value === null || value === undefined || value === '') return '';
      return String(value);
    },
    isPositiveNumber(value) {
      const num = Number(value);
      return Number.isFinite(num) && num > 0;
    },
    isImmuneRecord(item) {
      if (!item) return false;
      const desc = `${item.skillDesc || ''}${item.buffDesc || ''}${item.buffRoundEffect || ''}`;
      const hurt = this.getRecordNumber(item.hurt || item.hurtNum || item.damage);
      return (item.hurtType == 1 || item.hurtType == 2) && hurt <= 0 && desc.indexOf('免疫') !== -1;
    },
    isThornsRecord(item) {
      if (!item) return false;
      const text = `${item.skillName || ''}${item.skillDesc || ''}`;
      return text.indexOf('反伤') !== -1 || text.indexOf('打力') !== -1;
    },
    shouldShowSkillDesc(item) {
      if (!item || !item.skillDesc) return false;
      const desc = String(item.skillDesc).trim();
      if (!desc || desc === '技能攻击' || desc === '普通攻击') return false;
      return true;
    },
    getTimeCategoryLabel(category) {
      const map = {
        MAIN: '主伤',
        DOT: 'DOT',
        HOT: 'HOT',
        COUNTER: '反击',
        BUFF_TICK: '回合效果',
        CONTROL: '控制'
      };
      return map[category] || category || '';
    },
    getElementColor(element) {
      const colors = {
        '金属性伤害': '#d67411',
        '木属性伤害': '#04ad80',
        '水属性伤害': '#0f7ab4',
        '火属性伤害': '#d67411',
        '土属性伤害': '#0f7ab4',
        '风属性伤害': '#0f7ab4',
        '冰属性伤害': '#0fb479',
        '雷属性伤害': '#d67411'
      };
      return colors[element] || (this.isDark ? '#999' : '#666');
    },

    formatElement(element) {
      return element ? element.replace('属性伤害', '').replace('属性', '') : '';
    },
    isLegacyBuffDesc(item) {
      return item && item.buffEffect && (item.buffDesc === '自身增益' || item.buffDesc === '敌方减益');
    },
    hasActiveBuffs(item) {
      if (!item) return false;
      return !!this.parseActiveBuffs(item.activeBuffList).length;
    },
    hasTriggeredBuffs(item) {
      if (!item) return false;
      return !!(
        this.parseTriggeredEffectNames(item.buffEffect).length ||
        this.parseTriggeredDetails(item.buffRoundEffect).length
      );
    },
    isBuffExpanded(index) {
      return !!this.expandedBuffRows[index];
    },
    toggleBuffExpand(index) {
      this.$set(this.expandedBuffRows, index, !this.expandedBuffRows[index]);
    },
    parseActiveBuffs(buffListJson) {
      if (!buffListJson) return [];
      try {
        const buffs = JSON.parse(buffListJson);
        return Array.isArray(buffs) ? buffs : [];
      } catch (e) {
        return [];
      }
    },
    parseEffectNames(effectText) {
      if (!effectText) return [];
      return String(effectText)
        .split('|')
        .map(s => String(s || '').trim())
        .filter(Boolean)
        .map(s => s.replace(/^施加[:：]\s*/g, '').trim())
        .filter(Boolean);
    },
    parseTriggeredEffectNames(effectText) {
      if (!effectText) return [];
      return String(effectText)
        .split('|')
        .map(s => String(s || '').trim())
        .filter(Boolean)
        .filter(s => /^施加[:：]\s*/.test(s))
        .map(s => s.replace(/^施加[:：]\s*/g, '').trim())
        .filter(Boolean);
    },
    parseTriggeredDetails(roundEffectText) {
      if (!roundEffectText) return [];
      return String(roundEffectText)
        .split(';')
        .map(s => String(s || '').trim())
        .filter(Boolean)
        .filter(s => /^触发[:：]\s*/.test(s))
        .map(s => {
          let text = s;
          while (/^触发[:：]\s*/.test(text)) {
            text = text.replace(/^触发[:：]\s*/, '').trim();
          }
          return text;
        })
        .filter(Boolean);
    },
    formatTriggeredSummary(item) {
      const effectNames = this.parseTriggeredEffectNames(item && item.buffEffect);
      if (effectNames.length) {
        return effectNames.join(', ');
      }
      const details = this.parseTriggeredDetails(item && item.buffRoundEffect);
      return details.join(', ');
    },
    formatTriggeredDetail(item) {
      const details = this.parseTriggeredDetails(item && item.buffRoundEffect);
      if (details.length) {
        return details.join('; ');
      }
      const effectNames = this.parseTriggeredEffectNames(item && item.buffEffect);
      return effectNames.join(', ');
    },
    formatActiveBuffSummary(item) {
      const buffs = this.parseActiveBuffs(item.activeBuffList);
      if (!buffs.length) return '';
      return buffs.map(b => b.name || '状态').join(', ');
    },
    formatActiveBuffDetail(item) {
      const buffs = this.parseActiveBuffs(item && item.activeBuffList);
      if (!buffs.length) return '';
      return buffs.map(b => this.formatBuffDisplay(b)).join(', ');
    },

    // 新增：格式化buff列表
    formatBuffList(buffListJson) {
      try {
        const buffs = JSON.parse(buffListJson);
        return buffs.map(b => this.formatBuffDisplay(b)).join(', ');
      } catch (e) {
        return buffListJson;
      }
    },
    formatBuffDisplay(buff) {
      const name = (buff && buff.name) ? buff.name : '状态';
      const rounds = (buff && buff.rounds) ? buff.rounds : 0;
      const desc = (buff && buff.desc) ? String(buff.desc).trim() : '';
      const pending = !!(buff && buff.pending);
      const prefix = pending ? '待激活 ' : '';

      if (!desc) {
        return `${prefix}${name}(${rounds}回合)`;
      }

      return `${prefix}${name}[${desc}](${rounds}回合)`;
    }
  }
};
</script>

<style scoped>
/* 基础样式 - 完全保持不变 */
.battle-log {
  font-family: "Microsoft YaHei", "PingFang SC", monospace;
  font-size: 13px;
  line-height: 1.8;
  padding: 10px;
  max-height: 300px;
  overflow-y: auto;
}
.element{
  border-bottom: 1px dashed;
}
.log-row {
  overflow-y: auto;
  text-overflow: ellipsis;
  padding: 2px 0;
  position: relative;
  z-index: 1;
}

.log-row:hover {
  z-index: 10;
}

/* buff悬浮提示 */
.buff-tooltip {
  position: relative;
  cursor: pointer;
  color: #999;
  border-bottom: 1px solid;
}
.buff-toggle {
  margin-left: 4px;
  cursor: pointer;
  color: #7f8c8d;
  border-bottom: 1px dashed #7f8c8d;
}
.buff-detail {
  margin-left: 6px;
  color: #607d8b;
}
.trigger-text {
  color: inherit;
}
.active-text {
  color: inherit;
}

.buff-tooltip .tooltip-text {
  visibility: hidden;
  position: absolute;
  z-index: 1000;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  min-width: 170px;
  max-width: 250px;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  white-space: normal;
  text-align: center;
  opacity: 0;
  transition: opacity 0.3s;
  pointer-events: none;
}

.buff-tooltip:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}
/* ========== 黑夜模式 - 完全保持不变 ========== */
.dark {
  background: #1a1a1a;
  color: #ccc;
}

.dark .round {
  color: #666;
}

.dark .player {
  color: #4caf50;
}

.dark .enemy {
  color: #9c27b0;
}

.dark .target {
  color: #f44336;
}

.dark .damage {
  color: #ff9800;
  font-weight: bold;
}

.dark .immune {
  color: #9e9e9e;
  font-weight: bold;
}

.dark .damage-type {
  color: #888;
}

.dark .tag {
  display: inline-block;
  padding: 0 4px;
  border-radius: 2px;
  font-size: 11px;
  margin-right: 4px;
}

.dark .critical {
  background: #ff5722;
  color: #fff;
}

.dark .combo {
  background: #00bcd4;
  color: #fff;
}

.dark .timing {
  background: #607d8b;
  color: #fff;
}

.dark .shield {
  background: #009688;
  color: #fff;
}

.dark .skill {
  color: #2196f3;
}

.dark .normal {
  color: #888;
}

.dark .result {
  color: #888;
  margin-left: 8px;
}
.dark .trigger-text {
  color: #ffb74d;
}
.dark .active-text {
  color: #81c784;
}

.dark .dead {
  color: #f44336;
  font-weight: bold;
}

.dark .heal {
  color: #4caf50;
}

.dark .result-line {
  color: #ffd700;
  font-weight: bold;
}
.dark .buff-tooltip { color: #2196f3; }
.dark .tooltip-text { background: #2d2d2d; color: #ccc; border: 1px solid #444; }
/* ========== 白天模式 - 完全保持不变 ========== */
.light {
  background: #f5f5f5;
  color: #333;
}

.light .round {
  color: #999;
}

.light .player {
  color: #2e7d32;
  font-weight: 500;
}

.light .enemy {
  color: #7b1fa2;
  font-weight: 500;
}

.light .target {
  color: #c62828;
}

.light .damage {
  color: #e65100;
  font-weight: bold;
}

.light .immune {
  color: #607d8b;
  font-weight: bold;
}

.light .damage-type {
  color: #666;
}

.light .tag {
  display: inline-block;
  padding: 0 4px;
  border-radius: 2px;
  font-size: 11px;
  margin-right: 4px;
  border: 1px solid;
  background: transparent;
}

.light .critical {
  color: #d84315;
  border-color: #d84315;
  background: #fff3e0;
}

.light .combo {
  color: #00838f;
  border-color: #00838f;
  background: #e0f7fa;
}

.light .timing {
  color: #546e7a;
  border-color: #546e7a;
  background: #eceff1;
}

.light .shield {
  color: #00796b;
  border-color: #00796b;
  background: #e0f2f1;
}

.light .skill {
  color: #1565c0;
  font-weight: 500;
}

.light .normal {
  color: #757575;
}

.light .result {
  color: #757575;
  margin-left: 8px;
}
.light .trigger-text {
  color: #ef6c00;
}
.light .active-text {
  color: #2e7d32;
}

.light .dead {
  color: #c62828;
  font-weight: bold;
}

.light .heal {
  color: #2e7d32;
}

.light .result-line {
  color: #f9a825;
  font-weight: bold;
}
.light .buff-tooltip { color: #999; }
.light .tooltip-text { background: #fff; color: #333; border: 1px solid #ddd; box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
</style>



// WEBPACK FOOTER //
// src/components/battleProcess.vue